import { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { CloudConnectionEndpoints } from "@generated/cloud-connections";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

interface AwsSetupInfo {
  rushaAccountId: string;
  externalId: string;
  trustPolicyJson: string;
}

interface ValidationResult {
  success: boolean;
  message: string;
}

interface Props {
  organizationAlias: string;
}

export function AwsSetupPanel({ organizationAlias }: Props) {
  const [setupInfo, setSetupInfo] = useState<AwsSetupInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [roleArn, setRoleArn] = useState("");
  const [connectionName, setConnectionName] = useState("AWS");
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSetupInfo = async () => {
    setLoadingInfo(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.AWS_SETUP_INFO}?organizationAlias=${encodeURIComponent(organizationAlias)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      setSetupInfo(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load setup info");
    } finally {
      setLoadingInfo(false);
    }
  };

  const validate = async () => {
    if (!setupInfo || !roleArn) return;
    setValidating(true);
    setResult(null);
    setError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.AWS_VALIDATE}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            organizationAlias,
            name: connectionName || "AWS",
            roleArn,
            externalId: setupInfo.externalId,
          }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setResult(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        AWS Cross-Account Trust
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create an IAM role in your AWS account that Rusha can assume. Use the
        trust policy below when creating the role.
      </Typography>

      {!setupInfo && (
        <Button
          variant="outlined"
          onClick={fetchSetupInfo}
          disabled={loadingInfo}
          startIcon={loadingInfo ? <CircularProgress size={16} /> : undefined}
        >
          Generate trust policy
        </Button>
      )}

      {setupInfo && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Rusha AWS Account ID
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontFamily="monospace">
                {setupInfo.rushaAccountId}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(setupInfo.rushaAccountId)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              External ID (required in trust policy)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontFamily="monospace">
                {setupInfo.externalId}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(setupInfo.externalId)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                IAM Trust Policy JSON
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(setupInfo.trustPolicyJson)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              component="pre"
              sx={{
                p: 1.5,
                bgcolor: "grey.900",
                color: "grey.100",
                borderRadius: 1,
                fontSize: "0.75rem",
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {setupInfo.trustPolicyJson}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" sx={{ mb: 1 }}>
            After creating the role, provide a name and paste its ARN below to
            verify the connection:
          </Typography>
          <Box
            sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}
          >
            <TextField
              label="Connection name"
              placeholder="Production AWS"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              label="Role ARN"
              placeholder="arn:aws:iam::123456789012:role/RushaPlatformRole"
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={validate}
              disabled={validating || !roleArn}
              startIcon={
                validating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Verify
            </Button>
          </Box>
        </>
      )}

      {result && (
        <Alert
          severity={result.success ? "success" : "error"}
          icon={result.success ? <CheckCircleIcon /> : undefined}
          sx={{ mt: 2 }}
        >
          {result.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
